/**
 * ============================================================================
 * Membership Formatters - Business logic utilities
 * ============================================================================
 *
 * Extracted from JSX for testability and reusability
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from './currency';
import type { MembershipPackage, MembershipStatus, DurationType, Member } from '../types/memberships';

/**
 * Format currency amount
 */
export function formatCurrency(amount: number | string | undefined | null): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  if (isNaN(num)) return `${CURRENCY_SYMBOL}0`;
  return `${CURRENCY_SYMBOL}${num.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Format duration display
 */
export function formatDuration(duration: number, durationType: DurationType): string {
  const label = duration === 1 ? durationType : `${durationType}s`;
  return `${duration} ${label}`;
}

/**
 * Get status display info
 */
export function getStatusInfo(status: MembershipStatus): {
  label: string;
  colorClass: string;
} {
  const statusMap: Record<MembershipStatus, { label: string; colorClass: string }> = {
    active: { label: 'Active', colorClass: 'bg-green-100 text-green-700' },
    inactive: { label: 'Inactive', colorClass: 'bg-red-100 text-red-700' },
  };

  return statusMap[status] || statusMap.inactive;
}

/**
 * Export memberships to CSV
 */
export function exportMembershipsToCSV(packages: MembershipPackage[]): void {
  if (packages.length === 0) {
    throw new Error('No packages to export');
  }

  const headers = [
    'Package Name',
    'Description',
    'Price',
    'Duration',
    'Members',
    'Revenue',
    'Status',
    'Facilities Count',
  ];

  const rows = packages.map((pkg) => [
    pkg.name,
    pkg.description,
    pkg.price.toFixed(2),
    formatDuration(pkg.duration, pkg.duration_type),
    (pkg.members_count || 0).toString(),
    (pkg.total_revenue || 0).toFixed(2),
    getStatusInfo(pkg.status).label,
    pkg.facilities.length.toString(),
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  // Download CSV
  downloadCSV(csvContent, `membership_packages_${new Date().toISOString().split('T')[0]}.csv`);
}

/**
 * Export members to CSV
 */
export function exportMembersToCSV(members: Member[]): void {
  if (members.length === 0) {
    throw new Error('No members to export');
  }

  const headers = [
    'Name',
    'Email',
    'Phone',
    'Package',
    'Plan Type',
    'Status',
    'Start Date',
    'End Date',
    'Days Remaining',
    'Price',
  ];

  const rows = members.map((m) => {
    const status = m.is_expiring_soon ? 'Expiring Soon' : m.is_expired ? 'Expired' : m.is_active ? 'Active' : 'Inactive';
    return [
      m.user?.name || '',
      m.user?.email || '',
      m.user?.phone || '',
      m.package?.package_name || '',
      m.plan_type.charAt(0).toUpperCase() + m.plan_type.slice(1),
      status,
      m.start_date,
      m.end_date,
      m.days_remaining.toString(),
      m.price.toString(),
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  downloadCSV(csvContent, `members_${new Date().toISOString().split('T')[0]}.csv`);
}

function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
